import { inView } from 'motion'

var BaseMedia = class extends HTMLElement {
  static get observedAttributes() {
    return ["playing"]
  }
  connectedCallback() {
    this._abortController = new AbortController()
    if (this.hasAttribute("autoplay")) {
      inView(this, this.play.bind(this), { margin: "0px 0px 0px 0px" })
    }
  }
  disconnectedCallback() {
    this._abortController.abort()
  }
  get playing() {
    return this.hasAttribute("playing")
  }
  get player() {
    return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target
          this._playerHandler(target, prop)
        }
      }
    })
  }
  play() {
    if (!this.playing) {
      this.player.play()
    }
  }
  pause() {
    if (this.playing) {
      this.player.pause()
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }))
        if (this.hasAttribute("group")) {
          Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
            itemToPause.pause()
          })
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }))
      }
    }
  }
}

var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve()
})
var VideoMedia = class extends BaseMedia {
  #mustRemoveControlsAfterSuspend = false
  connectedCallback() {
    super.connectedCallback()
    if (!this.hasAttribute("autoplay")) {
      this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal })
    }
    if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(document.getElementById("video-media-default-template").content.cloneNode(true))
    }
    if (this.getAttribute("type") === "video") {
      inView(this, () => {
        this.querySelector("video")?.setAttribute("preload", "metadata")
      }, { margin: "800px" })
    }
  }
  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "")
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template")
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true))
        }
        const muteVideo = this.hasAttribute("autoplay") || matchesMediaQuery("md-max")
        const script = document.createElement("script")
        script.type = "text/javascript"
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api"
            document.head.appendChild(script)
            await new Promise((resolve2) => {
              script.onload = resolve2
            })
          }
          await onYouTubePromise
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              "onReady": () => {
                if (muteVideo) {
                  player.mute()
                }
                resolve(player)
              },
              "onStateChange": (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("playing", "")
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("playing")
                }
              }
            }
          })
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js"
            document.head.appendChild(script)
            await new Promise((resolve2) => {
              script.onload = resolve2
            })
          }
          const player = new Vimeo.Player(this.querySelector("iframe"))
          if (muteVideo) {
            player.setMuted(true)
          }
          player.on("play", () => {
            this.setAttribute("playing", "")
          })
          player.on("pause", () => this.removeAttribute("playing"))
          player.on("ended", () => this.removeAttribute("playing"))
          resolve(player)
        }
      })
    } else {
      const videoElement = this.querySelector("video")
      this.setAttribute("loaded", "")
      videoElement.addEventListener("play", () => {
        this.setAttribute("playing", "")
        this.removeAttribute("suspended")
        if (this.#mustRemoveControlsAfterSuspend) {
          videoElement.controls = false
        }
      })
      videoElement.addEventListener("pause", () => {
        if (!videoElement.seeking && videoElement.paused) {
          this.removeAttribute("playing")
        }
      })
      return videoElement
    }
  }
  _playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo()
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            this.setAttribute("suspended", "")
            if (!this.hasAttribute("controls")) {
              this.#mustRemoveControlsAfterSuspend = true
              target.controls = true
            }
          }
        })
      } else {
        target[prop]()
      }
    }
  }
}
if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia)
}